import React from 'react';
import styled from 'styled-components';
import SecondaryButton from '@carvana/showroom-forms/Button/Secondary';
import TrackerAnchor from '../../Tracker/TrackerAnchor';
import ErrorIcon from '../../../../assets/ErrorIcon';
import CheckIcon from '../../../../assets/CheckIcon';
import { LinkWithNativeEvents } from '../../../_common/link/link';

export const UploadButton = styled(SecondaryButton)`
  width: 100%;
`;

const Alert = styled.div`
  color: var(--cvna-color-blue-6);
  font-size: 16px;
  font-weight: 390;
  line-height: 24px;
  padding: 16px;
  display: flex;
  align-items: flex-start;
  gap: 8px;

  h5 {
    margin: 0;
    font-size: 16px;
    font-weight: 450;
  }

  p {
    margin: 0;
  }
`;

const Green = styled(Alert)`
  background: var(--cvna-color-green-1);
`;

const Red = styled(Alert)`
  background: var(--cvna-color-red-1);
`;

export const Link = styled.a<{
  href?: string;
  children?: any;
}>`
  color: inherit;
  text-decoration: underline;

  &:hover {
    color: inherit;
  }
`;

export const PhoneLink = styled(LinkWithNativeEvents)`
  text-decoration: none;
  color: inherit;

  &:hover {
    color: inherit;
  }
`;

type LinkTypes = {
  visible?: boolean;
  retry?: () => void;
  openDialog?: () => void;
};

const iconStyles = {
  width: '20px',
  height: '20px',
  transform: 'translateY(4px)',
};

const errorIconStyles = {
  ...iconStyles,
  color: 'var(--cvna-color-red-2)',
};

const successIconStyles = {
  ...iconStyles,
  color: 'var(--cvna-color-green-2)',
};

export const SuccessMessage = () => (
  <div>
    <Green>
      <div>
        <CheckIcon style={successIconStyles} />
      </div>
      <div>
        <h5>Upload successful</h5>
        <p>
          Thank you, we&apos;ve securely uploaded your documents to your account and our Customer Advocates will be
          reviewing them shortly.
        </p>
      </div>
    </Green>
  </div>
);

export const ErrorMessage = ({ visible, retry = () => {} }: LinkTypes) =>
  visible ? (
    <div>
      <Red>
        <div>
          <ErrorIcon style={errorIconStyles} />
        </div>
        <div>
          <h5>Upload error</h5>
          <p>
            Something went wrong. You can <TrackerAnchor onClick={retry}>retry your upload</TrackerAnchor> or contact us
            at{' '}
            <PhoneLink
              href="tel:18003334554"
              nativeEvent={{
                event: 'link',
                payload: {
                  url: 'tel:18003334554',
                },
              }}
            >
              1-800-333-4554
            </PhoneLink>{' '}
            for assistance.
          </p>
        </div>
      </Red>
    </div>
  ) : null;

export const DropContainer = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0;
  flex-direction: column;
  display: flex;

  @media (min-width: ${(props) => props.theme.breakpoint.md}px) {
    width: 100%;
    margin: 15px auto 0;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }
`;

export const Column = styled.div`
  flex: 1;
`;

export const DropInstructions = styled.div<{
  small?: boolean;
}>`
  font-size: ${(props) => (props.small ? '0.9rem' : '1 rem')};
  margin-bottom: ${(props) => (props.small ? '2px;' : '0')};
  @media (min-width: ${(props) => props.theme.breakpoint.md}px) {
    display: none;
  }
`;

export const Instructions = styled.div`
  text-align: left;
  margin: 0;
  color: var(--cvna-color-grey-5);
  font-size: 16px;
  line-height: 24px;

  .link {
    color: var(--cvna-color-blue-2);
    cursor: pointer;
  }
`;

export const InstructionsSmall = styled(Instructions)`
  font-size: 14px;
  line-height: 20px;
`;

export const InstructionsOuter = styled.div`
  text-align: center;
  margin: 0 auto 10px;
`;

export const FileList = styled.div`
  position: relative;
  text-align: left;
  padding-top: 15px;
  display: flex;
  flex-direction: column;

  @media (min-width: ${(props) => props.theme.breakpoint.md}px) {
    width: 100%;
    margin: 0;
  }
`;

const getBorderRules = (props: any) => {
  if (props.isMobile) {
    return 'none';
  }
  if (props.allowSubmit) {
    return 'border: 1px solid white';
  }
  return '1px solid var(--cvna-color-grey-2)';
};

const getBackgroundImageRules = (props: any) => {
  if (props.isMobile || !props.allowSubmit) {
    return 'none';
  }

  if (props.isDragAccept) {
    return `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23183558' stroke-width='4' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e")`;
  }

  return `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23aab1c7' stroke-width='4' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e")`;
};

export const Dropzone = styled.div<any>`
  display: flex;
  justify-content: space-around;
  flex-direction: column;
  gap: 24px;
  position: relative;
  text-align: left;

  @media (min-width: ${(props) => props.theme.breakpoint.md}px) {
    border-radius: 8px;
    padding: 24px;
    border: ${(props) => getBorderRules(props)};
    background-image: ${(props) => getBackgroundImageRules(props)};
  }

  ${(props) =>
    props.isDragAccept
      ? `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(255,255,255,0.8);
    }
  `
      : ``}
`;

export const LoadingDocument = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--cvna-color-white);
  border: 1px solid var(--cvna-color-grey-2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 450;
  line-height: 24px;
  color: var(--cvna-color-blue-6);

  > div {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }

  .loading-spinner {
    width: 80px;
    height: 80px;

    svg:first-of-type {
      width: 18px;
      height: 18px;
    }
  }
`;
